import { z, type ZodTypeAny, type infer as ZInfer } from "zod";
import { ApiError, NetworkError } from "./errors";

export interface ApiClientOptions {
  baseUrl?: string;
  getToken?: () => Promise<string | null> | string | null;
  /** 401 을 받았을 때 호출된다. 세션 정리는 여기서 (SA-06). */
  onUnauthorized?: (requestToken: string | null) => void | Promise<void>;
  /**
   * 403 을 받았을 때 호출된다.
   *
   * 만료된 토큰도 **403** 이다 (09 §AC-15) — 서버는 헤더가 아예 없을 때만
   * 401 을 준다. 그래서 403 하나로는 "권한이 없다" 와 "로그인이 끝났다" 를
   * 가를 수 없고, 앱은 로그인 상태를 유지한 채 모든 요청이 실패한다.
   *
   * 여기서 세션을 지우면 안 된다 — 관리자 화면을 기웃거린 일반 사용자가
   * 로그아웃된다. 대신 **한 번 물어보는** 자리다 (`Session.revalidate`).
   */
  onForbidden?: (requestToken: string | null) => void | Promise<void>;
  fetchImpl?: typeof fetch;
  /**
   * 한 요청을 이만큼 기다립니다. 0 이면 기다림에 끝이 없습니다.
   *
   * 이것이 없으면 서버가 응답을 **주지도 끊지도 않을 때** 화면이 영원히
   * "불러오는 중" 에 머뭅니다. 실제로 서버를 멈춰 놓고 확인했습니다 — 홈은
   * 계속 스켈레톤이었고, 여권은 토큰이 있는데도 "로그인하면 여권이 열립니다"
   * 라고 했습니다. 세션 확인이 끝나지 않아 `loading` 에 갇혀 있었습니다.
   *
   * 서버가 외부 출처를 부르는 경로(수집)는 그쪽 타임아웃이 10초라서,
   * 기본값은 그보다 넉넉하게 둡니다.
   */
  timeoutMs?: number;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  signal?: AbortSignal;
  /** 비로그인으로도 호출 가능한 경로 (Layer 1) */
  anonymous?: boolean;
  /** 이 요청만 다른 제한을 씁니다. 0 이면 제한 없음. */
  timeoutMs?: number;
}

/** 기본 요청 제한입니다. 수집 경로는 서버가 외부를 10초까지 기다립니다. */
const DEFAULT_TIMEOUT_MS = 20_000;

interface Envelope { data?: unknown; meta?: unknown; error?: { code?: string; message?: string; details?: Record<string, unknown> } }

export class ApiClient {
  readonly baseUrl: string;
  #getToken: ApiClientOptions["getToken"];
  #onUnauthorized: ApiClientOptions["onUnauthorized"];
  #onForbidden: ApiClientOptions["onForbidden"];
  #fetch: typeof fetch;
  #timeoutMs: number;

  constructor(opts: ApiClientOptions = {}) {
    this.baseUrl = (opts.baseUrl ?? "/api/v1").replace(/\/$/, "");
    this.#getToken = opts.getToken;
    this.#onUnauthorized = opts.onUnauthorized;
    this.#onForbidden = opts.onForbidden;
    this.#fetch = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.#timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  }

  /**
   * AC-02 교정: Legacy 는 `!payload.data` 를 실패로 봤다.
   * data 가 false / 0 / "" / [] 여도 성공이다. `data` 키 존재 여부로만 판정한다.
   */
  async request<S extends ZodTypeAny>(path: string, schema: S, opts: RequestOptions = {}): Promise<ZInfer<S>> {
    const raw = await this.#raw(path, opts);
    const parsed = schema.safeParse(raw);
    if (!parsed.success) {
      throw new ApiError(
        `응답 형태가 예상과 다릅니다: ${path}`,
        200, "SCHEMA_MISMATCH",
        { issues: parsed.error.issues.slice(0, 5) },
      );
    }
    return parsed.data;
  }

  /**
   * `data` 와 `meta` 를 함께 받습니다.
   *
   * 목록 API 는 `meta` 에 전체 개수 같은 것을 담습니다. `data` 만 꺼내 오면
   * 화면은 몇 건이 잘렸는지 알 수 없고, 운영자에게도 말해 줄 수 없습니다.
   */
  async requestEnvelope<D extends ZodTypeAny, M extends ZodTypeAny>(
    path: string, dataSchema: D, metaSchema: M, opts: RequestOptions = {},
  ): Promise<{ data: ZInfer<D>; meta: ZInfer<M> }> {
    const env = await this.#rawEnvelope(path, opts);
    const parsed = z.object({ data: dataSchema, meta: metaSchema }).safeParse(env);
    if (!parsed.success) {
      throw new ApiError(
        `응답 형태가 예상과 다릅니다: ${path}`,
        200, "SCHEMA_MISMATCH",
        { issues: parsed.error.issues.slice(0, 5) },
      );
    }
    return parsed.data as { data: ZInfer<D>; meta: ZInfer<M> };
  }

  /** 스키마 없이 원본 data 만 필요할 때. */
  async requestRaw(path: string, opts: RequestOptions = {}): Promise<unknown> {
    return this.#raw(path, opts);
  }

  async #raw(path: string, opts: RequestOptions): Promise<unknown> {
    return (await this.#rawEnvelope(path, opts)).data;
  }

  async #rawEnvelope(path: string, opts: RequestOptions): Promise<Envelope> {
    const url = this.baseUrl + path + buildQuery(opts.query);
    const headers: Record<string, string> = { Accept: "application/json" };
    let requestToken: string | null = null;
    if (opts.body !== undefined) headers["Content-Type"] = "application/json";
    if (!opts.anonymous) {
      const token = await this.#getToken?.();
      requestToken = token ?? null;
      if (token) headers["Authorization"] = `Bearer ${token}`;
    }

    /*
     * 부르는 쪽이 취소한 것과 우리가 기다리다 끊은 것은 다릅니다.
     * 취소는 AbortError 로 그대로 올려 보냅니다 — 화면이 그것을 보고 "내가
     * 취소했으니 무시" 라고 판단합니다. 시간 초과는 진짜 실패라서 오류로
     * 올립니다. 둘을 같은 것으로 만들면 시간 초과가 조용히 삼켜집니다.
     */
    const limit = opts.timeoutMs ?? this.#timeoutMs;
    const controller = new AbortController();
    const relay = () => controller.abort();
    if (opts.signal?.aborted) controller.abort();
    else opts.signal?.addEventListener("abort", relay, { once: true });
    let timedOut = false;
    const timer = limit > 0
      ? setTimeout(() => { timedOut = true; controller.abort(); }, limit)
      : undefined;

    let res: Response;
    let text: string;
    try {
      res = await this.#fetch(url, {
        method: opts.method ?? "GET",
        headers,
        body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
        signal: controller.signal,
        credentials: "include",
      });
      /* 응답 본문도 멈출 수 있습니다. 헤더만 받고 끝나지 않습니다. */
      text = await res.text();
    } catch (e) {
      if (timedOut) throw new NetworkError("서버가 제때 응답하지 않았습니다. 잠시 후 다시 시도해 주세요.");
      if ((e as Error)?.name === "AbortError") throw e;
      throw new NetworkError();
    } finally {
      if (timer !== undefined) clearTimeout(timer);
      opts.signal?.removeEventListener("abort", relay);
    }

    let env: Envelope | null = null;
    try { env = text ? (JSON.parse(text) as Envelope) : null; } catch { env = null; }

    if (res.status === 401) {
      /*
       * 비로그인 호출의 401 은 내 토큰에 대해 아무 말도 하지 않는다.
       * 여기서 세션을 지우면 소셜 로그인 복귀 같은 흐름이 오히려 로그아웃을 부른다.
       */
      if (!opts.anonymous) await this.#onUnauthorized?.(requestToken);
      throw new ApiError(env?.error?.message ?? "로그인이 필요합니다.", 401, env?.error?.code ?? "AUTH_REQUIRED", env?.error?.details ?? {});
    }
    if (res.status === 403 && !opts.anonymous) {
      /* 세션을 여기서 지우지 않는다 — 권한이 없을 뿐인 사람이 로그아웃된다.
         토큰이 아직 살아 있는지 확인하는 것은 듣는 쪽의 몫이다. */
      await this.#onForbidden?.(requestToken);
    }
    if (!res.ok) {
      throw new ApiError(
        env?.error?.message ?? statusMessage(res.status),
        res.status, env?.error?.code ?? "HTTP_ERROR", env?.error?.details ?? {},
      );
    }
    if (env === null) throw new ApiError("서버가 JSON 을 반환하지 않았습니다.", res.status, "NOT_JSON");
    if (env.error) throw new ApiError(env.error.message ?? "요청에 실패했습니다.", res.status, env.error.code ?? "ERROR", env.error.details ?? {});
    if (!("data" in env)) throw new ApiError("응답에 data 가 없습니다.", res.status, "NO_DATA");
    return env;
  }
}

/**
 * 서버가 봉투 없이 실패했을 때 쓰는 말.
 * 상태 코드를 그대로 보여주면 읽는 사람은 무엇을 해야 할지 알 수 없다 —
 * "(500)" 은 개발자의 언어다. 무슨 일이 일어났고 무엇을 하면 되는지 쓴다.
 */
function statusMessage(status: number): string {
  if (status === 404) return "찾는 정보가 없어요.";
  /*
   * 실측 (AC-15): 만료된 토큰도 **403** 이다 (401 이 아니다).
   * 서버는 헤더가 아예 없을 때만 401 을 준다. 그래서 403 을 "권한이 없어요" 라고만
   * 쓰면, 한 시간 지나 돌아온 사람에게 "너는 자격이 없다" 고 말하는 셈이 된다.
   * 권한 문제와 만료를 구분할 수 없으므로 **둘 다 맞는 말**을 쓴다.
   */
  if (status === 403) return "다시 로그인해 주세요. 로그인이 만료됐거나 권한이 없어요.";
  if (status === 408 || status === 504) return "응답이 너무 오래 걸렸어요. 잠시 후 다시 시도해 주세요.";
  if (status === 429) return "요청이 너무 많아요. 잠시 후 다시 시도해 주세요.";
  if (status >= 500) return "서버에 연결하지 못했어요. 잠시 후 다시 시도해 주세요.";
  if (status >= 400) return "요청을 처리하지 못했어요.";
  return "요청에 실패했습니다.";
}

function buildQuery(q?: RequestOptions["query"]): string {
  if (!q) return "";
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === "") continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}
